import React, { useState, useEffect } from 'react';
import AddRoomModal from './AddRoomModal';
import EditRoomModal from './EditRoomModal';

const ListingsModal = ({ show, onClose, property, rentalListings, onSave }) => {
  const [modalListings, setModalListings] = useState([]);
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);

  useEffect(() => {
    if (rentalListings && Array.isArray(rentalListings)) {
      setModalListings(rentalListings);
    }
  }, [rentalListings]);

  // Find the next available room number (max + 1, or 1 if none)
  const getNextRoomNumber = () => {
    if (modalListings.length === 0) return 1;
    const maxRoom = Math.max(...modalListings.map(l => Number(l.unit) || 0));
    return maxRoom + 1;
  };

  const handleAddListing = () => {
    const nextRoom = getNextRoomNumber();
    setModalListings([
      ...modalListings,
      {
        id: Date.now(),
        unit: nextRoom,
        rentalAgreements: [],
      },
    ]);
  };

  // Handle creating multiple rooms at once (now handles API response)
  const handleCreateRooms = async (count, responseData) => {
    let updatedListings;

    // If we got response data from the API, use it to update the listings
    if (responseData && responseData.listings) {
      // Map the new API response structure with full room details
      const newListings = responseData.listings.map(listing => ({
        id: listing.id,
        title: listing.title,
        description: listing.description || '',
        is_active: listing.is_active,
        room_id: listing.room_id,
        created_at: listing.created_at,
        updated_at: listing.updated_at,
        unit: listing.room?.room_number || listing.title, // Fallback to title if no room number
        rentalAgreements: [],
        ...listing
      }));

      updatedListings = [...modalListings, ...newListings];
      setModalListings(updatedListings);
    } else {
      // Fallback: create local listings (for backward compatibility)
      const newListings = [];
      let nextRoom = getNextRoomNumber();

      for (let i = 0; i < count; i++) {
        newListings.push({
          id: Date.now() + i,
          unit: nextRoom + i,
          rentalAgreements: [],
        });
      }

      updatedListings = [...modalListings, ...newListings];
      setModalListings(updatedListings);
    }

    // Immediately update the parent component with the new listings
    onSave(updatedListings);

    // Also refresh the entire rental listings from the server to ensure we have complete data
    await refreshRentalListings();
  };

  // Add function to refresh rental listings from server
  const refreshRentalListings = async () => {
    try {
      const agencyId = sessionStorage.getItem('agencyId');
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      if (!agencyId || !token || !property?.id) {
        return;
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const url = `${baseUrl}/agencies/${agencyId}/properties/${property.id}/rental-listings`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const refreshedListings = await response.json();
        const safeListings = Array.isArray(refreshedListings) ? refreshedListings : [];
        setModalListings(safeListings);
        onSave(safeListings);
        console.log('Rental listings refreshed successfully');
      }
    } catch (error) {
      console.error('Error refreshing rental listings:', error);
    }
  };


  const handleSave = () => {
    onSave(modalListings);
    onClose();
  };

    const handleClose = () => {
    setShowAddRoomModal(false); // Ensure nested modal is also closed
    onClose();
  };

  const handleCancel = () => {
    // Reset any unsaved changes
    if (rentalListings && Array.isArray(rentalListings)) {
      setModalListings(rentalListings);
    }
    handleClose();
  };

  const handleEditListing = (listing) => {
    setEditingListing(listing);
    setShowEditRoomModal(true);
  };

  const handleUpdateListing = async (updatedListing) => {
    // Update the local modal listings
    const updatedModalListings = modalListings.map(listing =>
      listing.id === updatedListing.id ? updatedListing : listing
    );
    setModalListings(updatedModalListings);

    // Also call onSave to update the parent component immediately
    onSave(updatedModalListings);

    setShowEditRoomModal(false);
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
      onClick={(e) => {
        // Close modal when clicking on backdrop
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: 800 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Listings</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <table className="table">
              <thead>
                <tr>
                  <th>Room Number</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {modalListings.map(listing => (
                  <tr key={listing.id}>
                    <td>{listing.title}</td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleEditListing(listing)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-success"
                onClick={() => setShowAddRoomModal(true)}
              >
                Add Room
              </button>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>

      {/* Add Room Modal */}
      <AddRoomModal
        show={showAddRoomModal}
        onClose={() => setShowAddRoomModal(false)}
        onCreateRooms={handleCreateRooms}
        property={property}
      />

      {/* Edit Room Modal */}
      <EditRoomModal
        show={showEditRoomModal}
        onClose={() => setShowEditRoomModal(false)}
        listing={editingListing}
        property={property}
        onSave={handleUpdateListing}
      />
    </div>
  );
};

export default ListingsModal;
